import React, { useState, useEffect } from 'react';
import Header from '../../components/admin/Header';
import Sidebar from '../../components/admin/Sidebar';
import Footer from '../../components/admin/Footer';
import { 
  User, 
  Mail, 
  Phone, 
  Save,
  X,
  Users as UsersIcon,
  FileText,
  BarChart3,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Eye,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Download,
  Filter,
  Search,
  ShoppingCart,
  AlertCircle,
  CheckCircle,
  Clock,
  Activity,
  LogIn,
  CreditCard,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';

// Chart components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const API_URL = 'https://api.marketmaestroovip.in/api';

const HomePage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeSessions: 0,
    pendingTasks: 0,
    totalRevenue: 0,
    successfulTransactions: 0,
    newUsersThisMonth: 0,
    monthlyRevenue: 0,
    revenueTrend: 0,
    userTrend: 0
  });
  
  const [monthlyData, setMonthlyData] = useState({ labels: [], revenue: [], users: [], transactions: [] });
  const [weeklyData, setWeeklyData] = useState({ labels: [], activeUsers: [], newUsers: [] });
  const [userStatus, setUserStatus] = useState({ Active: 0, Pending: 0, Inactive: 0 });
  const [topDocuments, setTopDocuments] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [paymentStats, setPaymentStats] = useState([]);
  const [revenueBreakdown, setRevenueBreakdown] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 5;

  // Fetch all dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [
        statsRes,
        monthlyRes,
        weeklyRes,
        userStatusRes,
        topDocsRes,
        usersRes,
        paymentRes,
        revenueRes
      ] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`),
        axios.get(`${API_URL}/dashboard/monthly-analytics`),
        axios.get(`${API_URL}/dashboard/weekly-activity`),
        axios.get(`${API_URL}/dashboard/user-status`),
        axios.get(`${API_URL}/dashboard/top-documents`),
        axios.get(`${API_URL}/dashboard/recent-users`),
        axios.get(`${API_URL}/dashboard/payment-analytics`),
        axios.get(`${API_URL}/dashboard/revenue-breakdown`)
      ]);

      setStats(statsRes.data);
      setMonthlyData(monthlyRes.data);
      setWeeklyData(weeklyRes.data);
      setUserStatus(userStatusRes.data);
      setTopDocuments(topDocsRes.data);
      setRecentUsers(usersRes.data);
      setPaymentStats(paymentRes.data);
      setRevenueBreakdown(revenueRes.data);
      
      console.log('Revenue Data:', statsRes.data.totalRevenue);
      console.log('Monthly Revenue:', monthlyRes.data.revenue);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setTimeout(() => setRefreshing(false), 500);
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Chart configurations
  const lineChartData = {
    labels: monthlyData.labels,
    datasets: [
      {
        label: 'Revenue (₹)',
        data: monthlyData.revenue,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      },
      {
        label: 'New Users',
        data: monthlyData.users,
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1',
      }
    ]
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top' },
      tooltip: { 
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            let value = context.raw;
            if (context.dataset.label.includes('Revenue')) {
              return `${label}: ₹${value.toLocaleString()}`;
            }
            return `${label}: ${value}`;
          }
        }
      }
    },
    scales: {
      y: { 
        beginAtZero: true, 
        title: { display: true, text: 'Revenue (₹)' },
        grid: { color: 'rgba(0, 0, 0, 0.05)' }
      },
      y1: { 
        position: 'right', 
        beginAtZero: true, 
        title: { display: true, text: 'Users' },
        grid: { drawOnChartArea: false }
      }
    }
  };

  const barChartData = {
    labels: weeklyData.labels,
    datasets: [
      {
        label: 'Active Users',
        data: weeklyData.activeUsers,
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderRadius: 6,
      },
      {
        label: 'New Users',
        data: weeklyData.newUsers,
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderRadius: 6,
      }
    ]
  };

  const pieChartData = {
    labels: Object.keys(userStatus),
    datasets: [{
      data: Object.values(userStatus),
      backgroundColor: ['rgba(16, 185, 129, 0.8)', 'rgba(245, 158, 11, 0.8)', 'rgba(239, 68, 68, 0.8)'],
      borderColor: ['rgb(16, 185, 129)', 'rgb(245, 158, 11)', 'rgb(239, 68, 68)'],
      borderWidth: 1,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' } }
  };

  // Filter users based on search
  const filteredUsers = recentUsers.filter(user =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  const statsCards = [
    { label: 'Total Users', value: stats.totalUsers.toLocaleString(), icon: UsersIcon, color: 'bg-blue-500', trend: `${stats.userTrend >= 0 ? '+' : ''}${stats.userTrend}% vs last month` },
    { label: 'Active Sessions', value: stats.activeSessions, icon: Activity, color: 'bg-green-500', trend: 'Currently active' },
    { label: 'Pending Payments', value: stats.pendingTasks, icon: Clock, color: 'bg-yellow-500', trend: 'Need attention' },
    { label: 'Total Revenue', value: `₹${stats.totalRevenue.toLocaleString()}`, icon: DollarSign, color: 'bg-purple-500', trend: `${stats.revenueTrend >= 0 ? '+' : ''}${stats.revenueTrend}% vs last month` },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex h-screen">
        <Sidebar isOpen={sidebarOpen} />
        <div className="flex-1 flex flex-col min-h-screen overflow-hidden lg:ml-64">
          <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
          
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Welcome Section */}
            <div className="mb-6 md:mb-8">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                    Dashboard Overview
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 mt-2">
                    Real-time analytics and insights from your platform
                  </p>
                  {stats.successfulTransactions > 0 && (
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                      {stats.successfulTransactions} successful transactions completed
                    </p>
                  )}
                </div>
                <button
                  onClick={refreshData}
                  disabled={refreshing}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
              {statsCards.map((stat, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                      <p className="text-2xl font-bold mt-2">{stat.value}</p>
                      <p className={`text-xs mt-2 ${stat.trend.includes('+') ? 'text-green-600' : stat.trend.includes('-') ? 'text-red-600' : 'text-blue-600'}`}>
                        {stat.trend}
                      </p>
                    </div>
                    <div className={`h-12 w-12 rounded-full ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Line Chart - Revenue & Users */}
              <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold">Revenue & User Growth</h3>
                    <p className="text-sm text-gray-500">Monthly performance overview</p>
                  </div>
                </div>
                <div className="h-80">
                  <Line data={lineChartData} options={lineChartOptions} />
                </div>
              </div>

              {/* Pie Chart - User Status */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200">
                <h3 className="text-lg font-bold mb-4">User Status Distribution</h3>
                <div className="h-64">
                  <Pie data={pieChartData} options={chartOptions} />
                </div>
                <div className="mt-4 space-y-2">
                  {Object.entries(userStatus).map(([status, count]) => (
                    <div key={status} className="flex justify-between items-center">
                      <span className="text-sm">{status}</span>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bar Chart - Weekly Activity */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold">Weekly User Activity</h3>
                  <p className="text-sm text-gray-500">Last 7 days performance</p>
                </div>
              </div>
              <div className="h-80">
                <Bar data={barChartData} options={chartOptions} />
              </div>
            </div>

            {/* Top Documents & Payment Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Top Documents */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    Top Selling Documents
                  </h3>
                </div>
                <div className="p-6">
                  {topDocuments.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No sales data available</p>
                      <p className="text-sm text-gray-400 mt-2">Sales will appear here once documents are purchased</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {topDocuments.map((doc, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{doc.title}</p>
                            <p className="text-sm text-gray-500">{doc.purchase_count} purchases</p>
                          </div>
                          <p className="font-semibold text-green-600">₹{parseFloat(doc.revenue).toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Statistics */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Payment Status Overview
                  </h3>
                </div>
                <div className="p-6">
                  {paymentStats.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No payment data available</p>
                      <p className="text-sm text-gray-400 mt-2">Payment records will appear here once transactions occur</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {paymentStats.map((payment, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {payment.payment_status === 'successful' ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : payment.payment_status === 'pending' || payment.payment_status === 'created' ? (
                              <Clock className="h-4 w-4 text-yellow-500" />
                            ) : (
                              <AlertCircle className="h-4 w-4 text-red-500" />
                            )}
                            <span className="capitalize">{payment.payment_status}</span>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{payment.count} transactions</p>
                            <p className="text-sm text-gray-500">₹{parseFloat(payment.total_amount || 0).toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Revenue Breakdown Table */}
            {revenueBreakdown.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 mb-8">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Monthly Revenue Breakdown
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-medium">Month</th>
                        <th className="text-left py-3 px-4 text-sm font-medium">Total Sales</th>
                        <th className="text-left py-3 px-4 text-sm font-medium">Revenue</th>
                        <th className="text-left py-3 px-4 text-sm font-medium">Unique Customers</th>
                        <th className="text-left py-3 px-4 text-sm font-medium">Unique Documents</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueBreakdown.map((item, idx) => (
                        <tr key={idx} className="border-b border-gray-200">
                          <td className="py-3 px-4 font-medium">{item.month}</td>
                          <td className="py-3 px-4">{item.total_sales}</td>
                          <td className="py-3 px-4 text-green-600 font-semibold">₹{parseFloat(item.revenue).toLocaleString()}</td>
                          <td className="py-3 px-4">{item.unique_customers}</td>
                          <td className="py-3 px-4">{item.unique_documents_sold}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent Users Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold">Recent Users</h2>
                    <p className="text-sm text-gray-500">Latest registered users</p>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Member</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Join Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentUsers.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-8 text-gray-500">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      currentUsers.map((user) => (
                        <tr key={user.id} className="border-b border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                                <User className="h-4 w-4 text-blue-600" />
                              </div>
                              <span className="font-medium">{user.username || 'N/A'}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">{user.email}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {user.status || 'pending'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {user.is_member ? '✓ Member' : 'Free User'}
                          </td>
                          <td className="py-3 px-4 text-sm">{user.join_date}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                      Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-2 rounded border disabled:opacity-50 hover:bg-gray-100"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="px-3 py-1 rounded bg-blue-600 text-white">{currentPage}</span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded border disabled:opacity-50 hover:bg-gray-100"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
          
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default HomePage;